// Gear item card — 4:3 portrait ratio
// - Single click → view
// - ... button (always visible, top-right) → context menu
// - Right-click → same context menu
// - Eye icon pill for visibility (colored by level)
// - Activity pills overlaid on photo
// - Brand · Size secondary line

import { useState, useRef, useEffect, useCallback } from 'react'
import { IconBox, IconEye, IconEyeOff, IconDotsVertical, IconPencil, IconTrash, IconBackpack, IconCopy, IconStar } from '@tabler/icons-react'

export type GearCardItem = {
  id: string
  title: string
  brand?: string
  size?: string | null
  condition?: string
  visibility: string
  isFavorite: boolean
  photos: { url: string; isPrimary: boolean }[]
  colors: string[]
  activities: string[]
}

// ── Unified visibility config — eye icons, semantic colors ───────────────────
// private=red, shared=orange, community=blue, public=green

export const VISIBILITY_CONFIG: Record<string, {
  label: string
  icon: React.ReactNode
  pill: string
  badge: string
}> = {
  private:   {
    label: 'Private',
    icon: <IconEyeOff size={11} />,
    pill:  'bg-green-950 text-green-400 border-green-800',
    badge: 'bg-green-950/80 text-green-400 border-green-800/60',
  },
  shared:    {
    label: 'Shared',
    icon: <IconEye size={11} />,
    pill:  'bg-orange-950 text-orange-400 border-orange-800',
    badge: 'bg-orange-950/80 text-orange-400 border-orange-800/60',
  },
  community: {
    label: 'Community',
    icon: <IconEye size={11} />,
    pill:  'bg-blue-950 text-blue-400 border-blue-800',
    badge: 'bg-blue-950/80 text-blue-400 border-blue-800/60',
  },
  public:    {
    label: 'Public',
    icon: <IconEye size={11} />,
    pill:  'bg-red-950 text-red-400 border-red-800',
    badge: 'bg-red-950/80 text-red-400 border-red-800/60',
  },
}

// ── Context menu ──────────────────────────────────────────────────────────────

type MenuAction = { label: string; icon: React.ReactNode; onClick: () => void; danger?: boolean }

function ContextMenu({ actions, onClose, position }: {
  actions: MenuAction[]
  onClose: () => void
  position: { x: number; y: number } | null
}) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [onClose])

  const style = position
    ? { position: 'fixed' as const, top: position.y, left: position.x, zIndex: 150 }
    : { position: 'absolute' as const, top: '100%', right: 0, zIndex: 150 }

  return (
    <div ref={ref} style={style}
      className="mt-1 w-44 bg-zinc-900 border border-border-strong rounded-xl shadow-2xl py-1 overflow-hidden">
      {actions.map((action, i) => (
        <button key={i} type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); action.onClick() }}
          className={[
            'w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors',
            action.danger
              ? 'text-red-400 hover:bg-red-950/40'
              : 'text-zinc-300 hover:bg-surface-raised',
          ].join(' ')}>
          <span className="shrink-0">{action.icon}</span>
          {action.label}
        </button>
      ))}
    </div>
  )
}

// ── Main GearCard ─────────────────────────────────────────────────────────────

type Props = {
  item: GearCardItem
  onView?: () => void
  onEdit?: () => void
  onAddToBag?: () => void
  onDuplicate?: () => void
  onToggleFavorite?: () => void
  onDelete?: () => void
}

export function GearCard({ item, onView, onEdit, onAddToBag, onDuplicate, onToggleFavorite, onDelete }: Props) {
  const primaryPhoto = item.photos.find(p => p.isPrimary) ?? item.photos[0]
  const stackPhotos = item.photos.filter(p => !p.isPrimary).slice(0, 2)
  const visCfg = VISIBILITY_CONFIG[item.visibility] ?? VISIBILITY_CONFIG.private

  const [menuOpen, setMenuOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null)
  const dotsRef = useRef<HTMLButtonElement>(null)

  const menuActions: MenuAction[] = [
    { label: 'View', icon: <IconEye size={14} />, onClick: () => onView?.() },
    { label: 'Edit', icon: <IconPencil size={14} />, onClick: () => onEdit?.() },
    { label: item.isFavorite ? 'Unfavorite' : 'Favorite', icon: <IconStar size={14} fill={item.isFavorite ? 'currentColor' : 'none'} />, onClick: () => onToggleFavorite?.() },
    { label: 'Duplicate', icon: <IconCopy size={14} />, onClick: () => onDuplicate?.() },
    { label: 'Add to Bag', icon: <IconBackpack size={14} />, onClick: () => onAddToBag?.() },
    { label: 'Delete', icon: <IconTrash size={14} />, onClick: () => onDelete?.(), danger: true },
  ]

  const openDotsMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuPos(null) // anchored to button
    setMenuOpen(v => !v)
  }, [])

  const openContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setMenuPos({ x: e.clientX, y: e.clientY })
    setMenuOpen(true)
  }, [])

  // Secondary info line — brand and/or size
  const secondaryParts = [item.brand, item.size].filter(Boolean)

  return (
    <div className="relative group">
      <button
        onClick={onView}
        onContextMenu={openContextMenu}
        className="relative flex flex-col bg-surface-overlay border border-border-default rounded-xl overflow-hidden hover:border-border-strong transition-all hover:shadow-lg hover:shadow-black/40 text-left w-full">

        {/* Photo area — 4:3 portrait */}
        <div className="relative" style={{ paddingBottom: '133%' }}>

          {/* Primary photo */}
          <div className="absolute inset-0">
            {primaryPhoto ? (
              <img src={primaryPhoto.url} alt={item.title}
                className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                <svg viewBox="0 0 80 80" className="w-16 h-16 opacity-30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="12" y="28" width="56" height="40" rx="4" stroke="white" strokeWidth="2.5"/>
                  <path d="M12 38h56" stroke="white" strokeWidth="2.5"/>
                  <path d="M28 28V22a12 12 0 0124 0v6" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
                  <circle cx="40" cy="49" r="4" stroke="white" strokeWidth="2"/>
                </svg>
              </div>
            )}
          </div>

          {/* Fanned stack — bottom right */}
          {stackPhotos.length > 0 && (
            <div className="absolute bottom-8 right-2" style={{ width: '25%', aspectRatio: '3/4' }}>
              {stackPhotos.slice().reverse().map((photo, i) => (
                <div key={i}
                  className="absolute inset-0 rounded-md overflow-hidden border border-white/20"
                  style={{
                    transform: `rotate(${(i + 1) * 7}deg) translateX(${(i + 1) * 3}px) translateY(${-(i + 1) * 1}px)`,
                    zIndex: stackPhotos.length - i,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.6)',
                  }}>
                  <img src={photo.url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
              <div className="absolute inset-0 rounded-md overflow-hidden border-2 border-white/30 z-10"
                style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.7)' }}>
                <img src={stackPhotos[stackPhotos.length - 1]?.url} alt="" className="w-full h-full object-cover" />
              </div>
              {/* Count badge */}
              <div className="absolute -top-2 -right-2 z-20 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-orange text-white text-[9px] font-bold flex items-center justify-center shadow-md border border-white/20">
                +{item.photos.length - 1}
              </div>
            </div>
          )}

          {/* Visibility badge — top left */}
          <div className={[
            'absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border backdrop-blur-sm',
            visCfg.badge,
          ].join(' ')}>
            {visCfg.icon}
            <span>{visCfg.label}</span>
          </div>

          {/* Activity pills — bottom left, above gradient */}
          {item.activities.length > 0 && (
            <div className="absolute bottom-2 left-2 flex flex-wrap gap-1 max-w-[70%]">
              {item.activities.slice(0, 2).map((act, i) => (
                <span key={i}
                  className="px-1.5 py-0.5 rounded-full text-[9px] bg-brand-orange/80 text-white border border-brand-orange/40 backdrop-blur-sm font-medium">
                  {act}
                </span>
              ))}
              {item.activities.length > 2 && (
                <span className="px-1.5 py-0.5 rounded-full text-[9px] bg-brand-orange/60 text-white/80 border border-brand-orange/30 backdrop-blur-sm">
                  +{item.activities.length - 2}
                </span>
              )}
            </div>
          )}

          {/* Favorite star — always visible, filled when favorited */}
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onToggleFavorite?.() }}
            className="absolute top-2 right-8 z-10 p-0.5 rounded-full transition-colors hover:scale-110"
            title={item.isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
            <IconStar
              size={15}
              className={item.isFavorite ? 'text-brand-orange drop-shadow-md' : 'text-white/70 drop-shadow-md hover:text-brand-orange'}
              fill={item.isFavorite ? 'currentColor' : 'none'}
              strokeWidth={2} />
          </button>

          {/* Gradient overlay */}
          <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />
        </div>

        {/* Info strip */}
        <div className="px-3 py-2.5">
          <div className="text-sm font-medium text-zinc-200 line-clamp-2">{item.title}</div>
          {secondaryParts.length > 0 && (
            <div className="text-xs text-zinc-500 truncate mt-0.5">
              {secondaryParts.join(' · ')}
            </div>
          )}
        </div>
      </button>

      {/* ... menu button — always visible */}
      <div className="absolute top-2 right-2 z-20">
        <div className="relative">
          <button
            ref={dotsRef}
            type="button"
            onClick={openDotsMenu}
            className="flex items-center justify-center w-6 h-6 rounded-full bg-black/50 border border-white/10 text-zinc-300 hover:bg-black/80 hover:text-white backdrop-blur-sm transition-colors">
            <IconDotsVertical size={12} />
          </button>
          {menuOpen && !menuPos && (
            <ContextMenu actions={menuActions} onClose={() => setMenuOpen(false)} position={null} />
          )}
        </div>
      </div>

      {/* Right-click context menu — fixed position */}
      {menuOpen && menuPos && (
        <ContextMenu actions={menuActions} onClose={() => { setMenuOpen(false); setMenuPos(null) }} position={menuPos} />
      )}
    </div>
  )
}

// ── GearHoverCard — rich preview on hover ────────────────────────────────────

type HoverCardProps = {
  item: GearCardItem
  visible: boolean
  anchorRect: DOMRect | null
}

export function GearHoverCard({ item, visible, anchorRect }: HoverCardProps) {
  if (!visible || !anchorRect) return null

  const visCfg = VISIBILITY_CONFIG[item.visibility] ?? VISIBILITY_CONFIG.private
  const primaryPhoto = item.photos.find(p => p.isPrimary) ?? item.photos[0]

  // Position: prefer right of card, fall back to left if near edge
  const spaceRight = window.innerWidth - anchorRect.right
  const cardWidth = 240
  const left = spaceRight > cardWidth + 16
    ? anchorRect.right + 8
    : anchorRect.left - cardWidth - 8
  const top = Math.min(anchorRect.top, window.innerHeight - 320)

  return (
    <div
      className="fixed z-[9000] pointer-events-none"
      style={{ left, top, width: cardWidth }}>
      <div className="bg-zinc-900 border border-border-strong rounded-2xl shadow-2xl overflow-hidden animate-fade-in">
        {/* Photo */}
        <div className="relative h-40 bg-zinc-800">
          {primaryPhoto ? (
            <img src={primaryPhoto.url} alt={item.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
              <svg viewBox="0 0 80 80" className="w-12 h-12 opacity-20" fill="none">
                <rect x="12" y="28" width="56" height="40" rx="4" stroke="white" strokeWidth="2.5"/>
                <path d="M12 38h56" stroke="white" strokeWidth="2.5"/>
                <path d="M28 28V22a12 12 0 0124 0v6" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
            </div>
          )}
          {/* Visibility badge */}
          <div className={`absolute top-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] border ${visCfg.badge}`}>
            {visCfg.icon}<span>{visCfg.label}</span>
          </div>
          {item.isFavorite && (
            <div className="absolute top-2 right-2">
              <IconStar size={13} className="text-brand-orange drop-shadow-md" fill="currentColor" />
            </div>
          )}
        </div>

        {/* Details */}
        <div className="p-3 space-y-2">
          <div>
            <div className="text-sm font-semibold text-zinc-100 leading-snug">{item.title}</div>
            {[item.brand, item.size].filter(Boolean).length > 0 && (
              <div className="text-xs text-zinc-500 mt-0.5">{[item.brand, item.size].filter(Boolean).join(' · ')}</div>
            )}
          </div>

          {item.condition && (
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-widest text-zinc-600">Condition</span>
              <span className="text-xs text-zinc-300 capitalize">{item.condition}</span>
            </div>
          )}

          {item.colors.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.colors.slice(0, 5).map((c, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded-full text-[10px] bg-zinc-800 text-zinc-400 border border-zinc-700">{c}</span>
              ))}
            </div>
          )}

          {item.activities.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {item.activities.slice(0, 3).map((a, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded-full text-[10px] bg-zinc-800 text-zinc-400 border border-zinc-700">{a}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
